import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, 
  Users, 
  UserCheck, 
  CheckCircle, 
  ArrowRight,
  Shield,
  ChevronDown,
  Book,
  Sparkles,
  Award,
  ClipboardList,
  Clock,
  Mail
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [showInstructions, setShowInstructions] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-600 via-purple-700 to-blue-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse delay-1000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-full p-6 shadow-2xl">
                <Heart className="w-16 h-16 text-white" fill="currentColor" />
              </div>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              HeartSync
              <span className="block text-purple-200 text-3xl md:text-4xl mt-2">
                Pre-Marriage Assessment and Counselling
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Build a stronger foundation for your marriage with comprehensive, faith-based assessment and counselling
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/couple/auth')}
                className="group px-8 py-4 bg-white text-purple-700 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center gap-2"
              >
                Start as a Couple
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              
              <button
                onClick={() => navigate('/counselor/auth')}
                className="px-8 py-4 bg-purple-800 bg-opacity-50 backdrop-blur text-white rounded-xl font-bold text-lg border-2 border-white border-opacity-30 hover:bg-opacity-70 transition-all"
              >
                Counselor Login
              </button>
            </div>

            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="mt-8 text-purple-200 hover:text-white flex items-center gap-2 mx-auto group"
            >
              How it works
              <ChevronDown className={`w-5 h-5 transition-transform ${showInstructions ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
      </section>

      {/* Quick Instructions (Collapsible) */}
      {showInstructions && (
        <section className="bg-purple-50 border-t-4 border-purple-200">
          <div className="max-w-5xl mx-auto px-6 py-12">
            <h2 className="text-3xl font-bold text-center mb-10 text-gray-800">
              Getting Started is Simple
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Partner 1 Flow */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-purple-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    1
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Partner 1 Registration</h3>
                </div>
                
                <ol className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>
                      Get{' '}
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 font-semibold rounded border border-blue-300">
                        counselor code
                      </span>
                      {' '}from your pre-marriage counselor
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Click "Start as a Couple" and select <strong>"Partner 1"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Enter counselor code and create your account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>
                      Copy the{' '}
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-800 font-semibold rounded border border-purple-300">
                        couple code
                      </span>
                      {' '}you receive
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Share couple code with your partner</span>
                  </li>
                </ol>
              </div>

              {/* Partner 2 Flow */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-blue-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    2
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Partner 2 Registration</h3>
                </div>
                
                <ol className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>
                      Get{' '}
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-800 font-semibold rounded border border-purple-300">
                        couple code
                      </span>
                      {' '}from Partner 1
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Click "Start as a Couple" and select <strong>"Partner 2"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Enter the couple code and create your account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Wait for counselor approval (usually within 24 hours)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Begin your assessment journey together!</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Two Programs Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Two Comprehensive Programs
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose the assessment path that fits your journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Pre-Marriage Intensive */}
            <div className="relative bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 border-2 border-emerald-200 overflow-hidden group hover:shadow-xl transition-shadow">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-200 rounded-full -mr-16 -mt-16 opacity-50"></div>
              
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-br from-emerald-500 to-teal-600 w-14 h-14 rounded-xl flex items-center justify-center shadow-lg">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Pre-Marriage Intensive</h3>
                    <span className="text-sm text-emerald-600 font-medium">10 Modules</span>
                  </div>
                </div>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  A deep-dive curriculum covering Kingdom marriage perspectives, intimacy, communication, finances, conflict resolution, and spiritual foundations.
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="text-2xl font-bold text-emerald-600">160</div>
                    <div className="text-sm text-gray-500">Questions</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="text-2xl font-bold text-emerald-600">100</div>
                    <div className="text-sm text-gray-500">Teaching Slides</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="text-2xl font-bold text-emerald-600">70</div>
                    <div className="text-sm text-gray-500">Prayer Points</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="text-2xl font-bold text-emerald-600">2</div>
                    <div className="text-sm text-gray-500">Prayer Formats</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {['Deep Prayer', 'Quick Prayer', 'Biblical Teaching', 'Practical Application'].map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm rounded-full font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* 20-Factor Assessment */}
            <div className="relative bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl p-8 border-2 border-purple-200 overflow-hidden group hover:shadow-xl transition-shadow">
              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-200 rounded-full -mr-16 -mt-16 opacity-50"></div>
              
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-br from-purple-500 to-blue-600 w-14 h-14 rounded-xl flex items-center justify-center shadow-lg">
                    <ClipboardList className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">20-Factor Assessment</h3>
                    <span className="text-sm text-purple-600 font-medium">20 Relationship Factors</span>
                  </div>
                </div>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Comprehensive evaluation covering all major relationship areas including communication, finances, faith, family background, intimacy, and more.
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="text-2xl font-bold text-purple-600">200</div>
                    <div className="text-sm text-gray-500">Questions</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="text-2xl font-bold text-purple-600">160</div>
                    <div className="text-sm text-gray-500">Teaching Slides</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="text-2xl font-bold text-purple-600">100</div>
                    <div className="text-sm text-gray-500">Prayer Points</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="text-2xl font-bold text-purple-600">20</div>
                    <div className="text-sm text-gray-500">Score Reports</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {['Compatibility Scores', 'Detailed Analysis', 'Growth Areas', 'Action Steps'].map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Why Choose HeartSync?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A comprehensive, faith-based approach to pre-marriage preparation
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
              <div className="bg-purple-600 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Book className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                360 Assessment Questions
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Comprehensive assessment covering communication, finances, faith, intimacy, conflict resolution, family dynamics, and more
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
              <div className="bg-blue-600 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Counselor-Guided
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Your counselor unlocks modules progressively and reveals results at the right time for maximum impact
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
              <div className="bg-pink-600 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-white" fill="currentColor" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Biblical Teaching & Prayer
              </h3>
              <p className="text-gray-600 leading-relaxed">
                260 teaching slides and 170 prayer points to strengthen your spiritual foundation together
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Detailed */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Your Assessment Journey
            </h2>
            <p className="text-xl text-gray-600">
              A step-by-step guided process for marriage preparation
            </p>
          </div>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="flex gap-6 items-start bg-gray-50 rounded-2xl p-8 shadow-sm">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-2xl flex items-center justify-center font-bold text-2xl shadow-lg">
                  1
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Register as a Couple</h3>
                <p className="text-gray-600 text-lg">
                  Partner 1 registers with{' '}
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 font-semibold rounded border border-blue-300">
                    counselor code
                  </span>
                  , receives{' '}
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 font-semibold rounded border border-purple-300">
                    couple code
                  </span>
                  . Partner 2 joins using{' '}
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 font-semibold rounded border border-purple-300">
                    couple code
                  </span>
                  . Both partners wait for counselor approval.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-6 items-start bg-gray-50 rounded-2xl p-8 shadow-sm">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-2xl flex items-center justify-center font-bold text-2xl shadow-lg">
                  2
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Complete Assessments</h3>
                <p className="text-gray-600 text-lg">
                  Your counselor unlocks modules and factors when ready. Work through the Pre-Marriage Intensive (10 modules) and 20-Factor Assessment together, with biblical teaching and prayer for each section.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-6 items-start bg-gray-50 rounded-2xl p-8 shadow-sm">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-green-700 text-white rounded-2xl flex items-center justify-center font-bold text-2xl shadow-lg">
                  3
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Review Results with Counselor</h3>
                <p className="text-gray-600 text-lg">
                  Your counselor reveals results when appropriate, discusses your compatibility scores, identifies growth areas, and guides you through action steps.
                </p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-6 items-start bg-gray-50 rounded-2xl p-8 shadow-sm">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-600 to-pink-700 text-white rounded-2xl flex items-center justify-center font-bold text-2xl shadow-lg">
                  4
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Build a Stronger Foundation</h3>
                <p className="text-gray-600 text-lg">
                  Use insights to strengthen your relationship, address concerns before marriage, and enter marriage with confidence and clarity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Updated with BOTH programs */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2">30</div>
              <div className="text-purple-200 text-lg">Modules & Factors</div>
              <div className="text-purple-300 text-sm mt-1">10 + 20</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">360</div>
              <div className="text-purple-200 text-lg">Assessment Questions</div>
              <div className="text-purple-300 text-sm mt-1">160 + 200</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">260</div>
              <div className="text-purple-200 text-lg">Teaching Slides</div>
              <div className="text-purple-300 text-sm mt-1">100 + 160</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">170</div>
              <div className="text-purple-200 text-lg">Prayer Points</div>
              <div className="text-purple-300 text-sm mt-1">70 + 100</div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* MARRIAGE READINESS SELF-TEST CTA SECTION */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        {/* Premium Dark Background with Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900" />
        
        {/* Animated Gradient Orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-rose-500/20 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-[-20%] right-[-10%] w-[400px] h-[400px] bg-violet-500/15 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-[30%] right-[20%] w-[200px] h-[200px] bg-pink-500/10 rounded-full blur-[60px] animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        {/* Grid Pattern Overlay */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {/* Left Column - Text Content */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-white/80 font-medium">Free Self-Assessment • No Registration</span>
              </div>

              {/* Heading */}
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Are You{' '}
                <span className="bg-gradient-to-r from-rose-400 via-pink-400 to-violet-400 bg-clip-text text-transparent">
                  Truly Ready
                </span>
                <br />
                for Marriage?
              </h2>

              {/* Description */}
              <p className="text-lg text-white/60 mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                Be brutally honest with yourself. Take our comprehensive 47-question self-test 
                across 8 critical areas — from emotional health to spiritual alignment.
              </p>

              {/* Features */}
              <div className="grid sm:grid-cols-3 gap-4 mb-10">
                {[
                  { icon: Clock, text: '5 Minutes' },
                  { icon: Shield, text: '100% Private' },
                  { icon: CheckCircle, text: 'Instant Results' }
                ].map((feature, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-center lg:justify-start gap-2 text-white/50"
                  >
                    <feature.icon className="w-4 h-4" />
                    <span className="text-sm">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <button
                onClick={() => navigate('/marriage-readiness-test')}
                className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-gradient-to-r from-rose-500 to-pink-600 text-white font-semibold text-lg shadow-2xl shadow-rose-500/25 hover:shadow-rose-500/40 transition-all duration-300 hover:scale-105"
              >
                <span>Take the Free Test</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                
                {/* Glossy shine effect */}
                <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent" style={{ height: '50%' }} />
                </div>
              </button>

              <p className="mt-4 text-sm text-white/40 flex items-center justify-center lg:justify-start gap-2">
                <Mail className="w-4 h-4" />
                Results sent directly to your email
              </p>
            </div>

            {/* Right Column - Visual Card Preview */}
            <div className="relative hidden lg:block">
              {/* Main Card */}
              <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 shadow-2xl overflow-hidden">
                {/* Glossy shine */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" style={{ height: '40%' }} />
                
                <div className="relative">
                  {/* Header */}
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 shadow-lg shadow-rose-500/30 mb-4">
                      <Heart className="w-8 h-8 text-white" fill="currentColor" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-1">Marriage Readiness</h3>
                    <p className="text-white/50 text-sm">Self-Assessment Test</p>
                  </div>

                  {/* Sample Sections Preview */}
                  <div className="space-y-3">
                    {[
                      { name: 'Motive Check', color: 'from-rose-500 to-pink-600' },
                      { name: 'Emotional Health', color: 'from-violet-500 to-purple-600' },
                      { name: 'Self-Awareness', color: 'from-amber-500 to-orange-600' },
                      { name: 'Spiritual Alignment', color: 'from-sky-500 to-blue-600' },
                      { name: 'Character Check', color: 'from-emerald-500 to-teal-600' }
                    ].map((section, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${section.color} flex items-center justify-center text-xs font-bold text-white`}>
                            {index + 1}
                          </div>
                          <span className="text-sm text-white/80">{section.name}</span>
                        </div>
                        <span className="text-white/30 text-sm">—</span>
                      </div>
                    ))}
                    
                    {/* "And more" indicator */}
                    <div className="text-center pt-2">
                      <span className="text-white/30 text-sm">+ 3 more sections</span>
                    </div>
                  </div>

                  {/* Bottom Stats */}
                  <div className="flex justify-center gap-6 mt-8 pt-6 border-t border-white/10">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white">47</div>
                      <div className="text-xs text-white/40">Questions</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white">8</div>
                      <div className="text-xs text-white/40">Sections</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white">5</div>
                      <div className="text-xs text-white/40">Minutes</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full blur-2xl opacity-20" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full blur-2xl opacity-15" />
            </div>
          </div>
        </div>
      </section>
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* END MARRIAGE READINESS CTA SECTION */}
      {/* ═══════════════════════════════════════════════════════════════ */}

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Heart className="w-20 h-20 text-purple-600 mx-auto mb-6" fill="currentColor" />
          <h2 className="text-4xl font-bold text-gray-800 mb-6">
            Ready to Strengthen Your Foundation?
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Join couples who are investing in their marriage before saying "I do"
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/couple/auth')}
              className="group px-10 py-5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center gap-3"
            >
              <Users className="w-6 h-6" />
              Start as a Couple
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            
            <button
              onClick={() => navigate('/counselor/auth')}
              className="px-10 py-5 bg-white text-blue-600 rounded-xl font-bold text-lg border-2 border-blue-600 hover:bg-blue-50 transition-all flex items-center justify-center gap-3"
            >
              <UserCheck className="w-6 h-6" />
              Counselor Access
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="w-6 h-6 text-purple-400" fill="currentColor" />
            <span className="text-xl font-bold text-white">HeartSync</span>
          </div>
          <p className="mb-4">Building stronger marriages through comprehensive pre-marriage assessment and counselling</p>
          <p className="text-sm text-gray-500">
            © 2025 HeartSync. All rights reserved. | RCCG Living Spring Morgantown
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
